import { useMemo, useState } from "react";
import { istTime } from "../../domain/istTime";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { ApiError } from "../../api/errors";
import { shortName } from "../../auth/scope";
import { formatInrPaise } from "../../domain/tiers";
import { getPinOffer, gstLabel, publishesWholesalePins, type PinPlan } from "../../api/pinPlans";
import { useProviderKind } from "./useProviderKind";
import { PinSellersSection } from "./PinBuyerLinks";
import { PinBackedPlansSection } from "./PinBackedPlans";
import { BuyerWalletSection, SellerWalletSection } from "./Wallet";
import {
  PIN_ORDER_STATUS_LABEL,
  canInvoicePinOrder,
  isAccessOrder,
  getPinOrderInvoice,
  raisePinOrderInvoice,
  getPinOrderVoucher,
  issuePinOrderVoucher,
  PIN_STATUS_LABEL,
  approvePinOrder,
  canRevokePin,
  createPinOrder,
  listIncomingPinOrders,
  listIssuedPins,
  listPinOrders,
  listPins,
  newIdempotencyKey,
  pinOrderActions,
  pinOrderStatusColour,
  pinOrderTotals,
  pinStatusColour,
  pinWalletCount,
  quantityError,
  recordPinOrderPayment,
  rejectPinOrder,
  revokePin,
  type Pin,
  type PinOrder,
  type PinOrderVoucher,
} from "../../api/pinOrders";
import { getCapability, pinSaleDocuments, type PinSaleDocument } from "../../api/capability";

const inr = (paise: number) => formatInrPaise(paise / 100);
const when = istTime;
const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

/**
 * Providers › Pins (owner, step 3 of the pin plan, 2026-09-19).
 *
 * 🔑 Paying does not credit the wallet. A PIN sub-operator orders and pays;
 * its PARENT records the payment reference and approves here; only then are
 * the pins issued, each with its own key, visible to both sides.
 *
 * Which view this is comes from radaccess (/pinplans/offered says whether this
 * namespace is a pin sub-operator), never guessed from the namespace's shape.
 */
export function PinOrdersScreen() {
  const identity = useIdentity();
  const ns = identity.namespace ?? "";
  const offer = useQuery({
    queryKey: ["pinoffer", ns],
    enabled: Boolean(identity.token && ns),
    queryFn: ({ signal }) => getPinOffer(identity, ns, signal),
    retry: false,
  });
  const licensed = publishesWholesalePins(useProviderKind());

  if (offer.isLoading) return <Typography color="text.secondary">Loading…</Typography>;
  if (offer.error) return <Alert severity="error">{errText(offer.error)}</Alert>;
  if (offer.data?.suboperator && (offer.data.kind === "pin" || offer.data.kind === "site")) {
    return <BuyerView ns={ns} parent={offer.data.parent ?? ""} suspended={offer.data.suspended} plans={offer.data.plans} site={offer.data.kind === "site"} />;
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <SellerView ns={ns} />
      {/* Only a licensed ISP/VNO sells priced pins, so only it prices wallet activations (owner 2026-10-15). */}
      {licensed && <SellerWalletSection ns={ns} />}
      {licensed && <LinkedBuyerView ns={ns} plans={offer.data?.linkedplans ?? []} />}
    </Box>
  );
}

/* ----------------------------------------------------- linked buyer */

/**
 * A licensed ISP or VNO buying pins from another one that linked it (owner
 * 2026-10-10, the VNO "mixed bag"). Same order flow as a PIN sub-operator --
 * pay, the seller records it and approves -- but the seller is named on every
 * order, because there may be more than one.
 */
function LinkedBuyerView({ ns, plans }: { ns: string; plans: PinPlan[] }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const [buying, setBuying] = useState(false);
  const orders = useQuery({
    queryKey: ["pinorders", ns],
    enabled: Boolean(identity.token && ns),
    queryFn: ({ signal }) => listPinOrders(identity, ns, signal),
  });
  const wallet = useQuery({
    queryKey: ["pins", ns],
    enabled: Boolean(identity.token && ns),
    queryFn: ({ signal }) => listPins(identity, ns, signal),
  });
  const counts = pinWalletCount(wallet.data ?? []);
  const bought = (orders.data ?? []).length > 0 || (wallet.data ?? []).length > 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" component="h2">
            Buy pins from other operators
          </Typography>
          <Typography variant="body2" color="text.secondary">
            From an ISP or VNO that added you as a pin buyer. Pay it, then it approves the order and the pins appear here.
          </Typography>
        </Box>
        <Button variant="contained" disabled={plans.length === 0} onClick={() => setBuying(true)}>
          Buy pins
        </Button>
      </Box>

      <PinSellersSection buyer={ns} />

      {plans.length === 0 && !bought && (
        <Alert severity="info">No operator offers you pins yet. An ISP or VNO must add you as a pin buyer, and you accept.</Alert>
      )}

      {bought && (
        <>
          <Stack direction="row" spacing={1}>
            <Chip color="success" label={`${counts.available} available`} />
            <Chip label={`${counts.used} used`} />
            <Chip color={counts.closed ? "error" : "default"} label={`${counts.closed} locked or revoked`} />
          </Stack>
          <Section title="Your orders" error={orders.error}>
            <OrderTable orders={orders.data ?? []} showParty="seller" empty="You have not ordered any pins yet." />
          </Section>
          <Section title="Your pins" error={wallet.error}>
            <PinTable pins={wallet.data ?? []} showHolder={false} empty="No pins yet. An approved order puts them here." />
          </Section>
          <PinBackedPlansSection ns={ns} />
          <BuyerWalletSection ns={ns} />
        </>
      )}

      {buying && (
        <BuyDialog
          ns={ns}
          plans={plans}
          linked
          onClose={() => setBuying(false)}
          onPlaced={() => {
            void qc.invalidateQueries({ queryKey: ["pinorders", ns] });
            setBuying(false);
          }}
        />
      )}
    </Box>
  );
}

/* ------------------------------------------------------------------ buyer */

function BuyerView({ ns, parent, suspended, plans, site }: { ns: string; parent: string; suspended: boolean; plans: PinPlan[]; site: boolean }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const [buying, setBuying] = useState(false);

  const orders = useQuery({
    queryKey: ["pinorders", ns],
    enabled: Boolean(identity.token && ns),
    queryFn: ({ signal }) => listPinOrders(identity, ns, signal),
  });
  const wallet = useQuery({
    queryKey: ["pins", ns],
    enabled: Boolean(identity.token && ns),
    queryFn: ({ signal }) => listPins(identity, ns, signal),
  });

  const counts = pinWalletCount(wallet.data ?? []);
  const parentName = parent ? shortName(parent) : "your parent operator";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" component="h1">
            Pins
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {site
              ? `Order access pins from ${parentName}, at no charge. ${parentName} approves the order and the pins appear here.`
              : `Buy pins from ${parentName}. Pay, then ${parentName} approves the order and the pins appear here — paying does not add them by itself.`}
          </Typography>
        </Box>
        <Button variant="contained" disabled={suspended || plans.length === 0} onClick={() => setBuying(true)}>
          {site ? "Order pins" : "Buy pins"}
        </Button>
      </Box>

      {suspended && <Alert severity="warning">{parentName} has suspended your account. You cannot order pins while it is suspended.</Alert>}
      {!suspended && plans.length === 0 && <Alert severity="info">{parentName} has not published a pin plan for you yet.</Alert>}

      <Stack direction="row" spacing={1}>
        <Chip color="success" label={`${counts.available} available`} />
        <Chip label={`${counts.used} used`} />
        <Chip color={counts.closed ? "error" : "default"} label={`${counts.closed} locked or revoked`} />
      </Stack>

      <Section title="Your orders" error={orders.error}>
        <OrderTable orders={orders.data ?? []} showParty="seller" empty="You have not ordered any pins yet." />
      </Section>

      <Section title="Your pins" error={wallet.error}>
        <PinTable pins={wallet.data ?? []} showHolder={false} empty="No pins yet. An approved order puts them here." />
      </Section>

      {/* A PIN provider and a Network Operator site both run only on pins (owner 2026-10-10). */}
      <PinBackedPlansSection ns={ns} mandatory />

      {/* Owner 2026-10-15: pins first, then the wallet. A site holds only access pins, never money. */}
      {!site && <BuyerWalletSection ns={ns} />}

      {buying && (
        <BuyDialog
          ns={ns}
          plans={plans}
          onClose={() => setBuying(false)}
          onPlaced={() => {
            void qc.invalidateQueries({ queryKey: ["pinorders", ns] });
            setBuying(false);
          }}
        />
      )}
    </Box>
  );
}

function BuyDialog({ ns, plans, linked = false, onClose, onPlaced }: { ns: string; plans: PinPlan[]; linked?: boolean; onClose: () => void; onPlaced: () => void }) {
  const identity = useIdentity();
  const [planId, setPlanId] = useState(plans[0]?.id ?? "");
  const [qty, setQty] = useState("1");
  // 🔑 One key per dialog, not per render: a key that changed as you typed
  // would turn every retry into a second order.
  const idempotencyKey = useMemo(() => newIdempotencyKey(), []);

  const plan = plans.find((p) => p.id === planId);
  const qtyError = quantityError(qty);
  const totals = plan && !qtyError ? pinOrderTotals(plan.pricepaise, Number(qty), plan.priceincludesgst, plan.gstratebp) : null;

  const place = useMutation({
    // A linked order names its seller -- the plan's own namespace.
    mutationFn: () => createPinOrder(identity, ns, planId, Number(qty), idempotencyKey, linked ? plan?.namespace : undefined),
    onSuccess: onPlaced,
  });

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{plans.every((p) => p.kind === "access") ? "Order access pins" : "Buy pins"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField select label="Plan" value={planId} onChange={(e) => setPlanId(e.target.value)} fullWidth>
            {plans.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {linked ? `${shortName(p.namespace)}: ` : ""}
                {p.name} — up to {p.speedmbps} Mbps, {p.termdays} days, {p.kind === "access" ? "no charge" : `${inr(p.grosspaise)} a pin`}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="How many pins"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            error={Boolean(qtyError)}
            helperText={qtyError ?? "One pin activates or renews one subscriber, once."}
            fullWidth
          />
          {plan && totals && plan.kind !== "access" && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {qty} × {inr(plan.pricepaise)} {plan.priceincludesgst ? "(GST included)" : "(GST extra)"}
              </Typography>
              <Typography variant="body2">Net {inr(totals.net)}</Typography>
              <Typography variant="body2">
                GST {gstLabel(plan.gstratebp)} {inr(totals.gst)}
              </Typography>
              <Typography variant="h6">Total {inr(totals.gross)}</Typography>
            </Paper>
          )}
          <Alert severity="info">
            {plan?.kind === "access"
              ? "Placing the order does not issue the pins. Your network operator approves it, and the pins appear only then. Nothing is charged."
              : linked
                ? `Placing the order does not issue the pins. Pay ${plan ? shortName(plan.namespace) : "the seller"}, then it records the payment and approves — the pins appear only then.`
                : "Placing the order does not issue the pins. Pay your parent operator, then it records the payment and approves — the pins appear only then."}
          </Alert>
          {place.error && <Alert severity="error">{errText(place.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!plan || Boolean(qtyError) || place.isPending} onClick={() => place.mutate()}>
          Place order
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ----------------------------------------------------------------- seller */

function SellerView({ ns }: { ns: string }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const [acting, setActing] = useState<{ order: PinOrder; action: "payment" | "approve" | "reject" } | null>(null);
  const [revoking, setRevoking] = useState<Pin | null>(null);
  const [invoicing, setInvoicing] = useState<PinOrder | null>(null);
  const [vouchering, setVouchering] = useState<PinOrder | null>(null);

  // An active Internet Distributor also issues vouchers (owner 2026-10-26).
  // Staff without operatorcapability:read get a refusal: they keep the
  // invoice alone, which is what radbilling would allow them anyway.
  const capability = useQuery({
    queryKey: ["capability", ns],
    enabled: Boolean(identity.token && ns),
    queryFn: async () => (await getCapability(identity, ns)).data,
    retry: false,
  });
  const documents = pinSaleDocuments(capability.data);

  const incoming = useQuery({
    queryKey: ["pinorders-incoming", ns],
    enabled: Boolean(identity.token && ns),
    queryFn: ({ signal }) => listIncomingPinOrders(identity, ns, signal),
  });
  const issued = useQuery({
    queryKey: ["pins-issued", ns],
    enabled: Boolean(identity.token && ns),
    queryFn: ({ signal }) => listIssuedPins(identity, ns, signal),
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["pinorders-incoming", ns] });
    void qc.invalidateQueries({ queryKey: ["pins-issued", ns] });
  };

  const waiting = (incoming.data ?? []).filter((o) => o.status === "paid" || o.status === "awaiting_approval").length;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box>
        <Typography variant="h5" component="h1">
          Pin orders
        </Typography>
        <Typography variant="body2" color="text.secondary">
          What your PIN sub-operators and pin buyers have ordered. Record the payment you received, then approve — approving is what issues the pins.
        </Typography>
      </Box>

      {waiting > 0 && <Alert severity="info">{waiting === 1 ? "1 paid order is" : `${waiting} paid orders are`} waiting for your approval.</Alert>}

      <Section title="Orders placed with you" error={incoming.error}>
        <OrderTable
          orders={incoming.data ?? []}
          showParty="buyer"
          empty="No sub-operator has ordered pins from you yet."
          onAct={(order, action) => setActing({ order, action })}
          onInvoice={setInvoicing}
          onVoucher={setVouchering}
          documents={documents}
        />
      </Section>

      <Section title="Pins you issued" error={issued.error}>
        <PinTable pins={issued.data ?? []} showHolder onRevoke={setRevoking} empty="No pins issued yet." />
      </Section>

      {acting && <OrderActionDialog ns={ns} order={acting.order} action={acting.action} onClose={() => setActing(null)} onDone={() => { refresh(); setActing(null); }} />}
      {revoking && <RevokeDialog ns={ns} pin={revoking} onClose={() => setRevoking(null)} onDone={() => { refresh(); setRevoking(null); }} />}
      {invoicing && <InvoiceDialog ns={ns} order={invoicing} onClose={() => setInvoicing(null)} />}
      {vouchering && <VoucherDialog ns={ns} order={vouchering} onClose={() => setVouchering(null)} />}
    </Box>
  );
}

const ACTION_COPY = {
  payment: {
    title: "Record the payment",
    field: "Payment reference",
    help: "The UPI reference, bank UTR or receipt number you received. It is shown to the sub-operator.",
    confirm: "Record payment",
    note: "Recording the payment does not issue any pin. You approve next.",
  },
  approve: {
    title: "Approve and issue the pins",
    field: "",
    help: "",
    confirm: "Approve and issue",
    note: "This issues the pins now, each with its own key. It cannot be undone — an issued pin is revoked one at a time.",
  },
  reject: {
    title: "Reject the order",
    field: "Reason",
    help: "Shown to the sub-operator. No pin is issued, and any refund is between you and them.",
    confirm: "Reject",
    note: "",
  },
} as const;

function OrderActionDialog({ ns, order, action, onClose, onDone }: { ns: string; order: PinOrder; action: "payment" | "approve" | "reject"; onClose: () => void; onDone: () => void }) {
  const identity = useIdentity();
  const [text, setText] = useState("");
  const copy = ACTION_COPY[action];

  const run = useMutation({
    mutationFn: () => {
      if (action === "payment") return recordPinOrderPayment(identity, ns, order.id, text.trim());
      if (action === "reject") return rejectPinOrder(identity, ns, order.id, text.trim());
      return approvePinOrder(identity, ns, order.id);
    },
    onSuccess: onDone,
  });

  const needsText = copy.field !== "";

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{copy.title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {shortName(order.buyer)} — {order.quantity} × {order.planname} (up to {order.speedmbps} Mbps, {order.termdays} days){isAccessOrder(order) ? "" : `, ${inr(order.grosspaise)}`}
          </Typography>
          {needsText && <TextField label={copy.field} helperText={copy.help} value={text} onChange={(e) => setText(e.target.value)} fullWidth autoFocus />}
          {copy.note && <Alert severity={action === "approve" ? "warning" : "info"}>{copy.note}</Alert>}
          {run.error && <Alert severity="error">{errText(run.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color={action === "reject" ? "error" : "primary"} disabled={run.isPending || (needsText && !text.trim())} onClick={() => run.mutate()}>
          {copy.confirm}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RevokeDialog({ ns, pin, onClose, onDone }: { ns: string; pin: Pin; onClose: () => void; onDone: () => void }) {
  const identity = useIdentity();
  const [reason, setReason] = useState("");
  const run = useMutation({
    mutationFn: () => revokePin(identity, ns, pin.id, reason.trim()),
    onSuccess: onDone,
  });

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Revoke pin {pin.key}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Held by {shortName(pin.holder)}, unused. Revoking closes the key for good; it can never be applied.
          </Typography>
          <TextField label="Reason" helperText="Shown to the sub-operator with its funds." value={reason} onChange={(e) => setReason(e.target.value)} fullWidth autoFocus />
          <Alert severity="info">Any refund for a revoked pin is between you and your sub-operator, under your own agreement.</Alert>
          {run.error && <Alert severity="error">{errText(run.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="error" disabled={run.isPending || !reason.trim()} onClick={() => run.mutate()}>
          Revoke
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * The invoice for an approved order. Reading never raises one -- a read that
 * quietly numbered a document would be a GET with a statutory side effect --
 * so an order that has not been invoiced shows the button, not an error.
 */
function InvoiceDialog({ ns, order, onClose }: { ns: string; order: PinOrder; onClose: () => void }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const key = ["pinorder-invoice", ns, order.id];
  const invoice = useQuery({
    queryKey: key,
    enabled: Boolean(identity.token && ns),
    queryFn: ({ signal }) => getPinOrderInvoice(identity, ns, order.id, signal),
    retry: false,
  });
  const raise = useMutation({
    mutationFn: () => raisePinOrderInvoice(identity, ns, order.id),
    onSuccess: (inv) => qc.setQueryData(key, inv),
  });

  const inv = invoice.data;
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Invoice for {order.quantity} pins</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {shortName(order.buyer)} — {order.planname}, approved {when(order.decidedat)}
          </Typography>
          {invoice.isLoading && <Typography color="text.secondary">Loading…</Typography>}
          {invoice.error && <Alert severity="error">{errText(invoice.error)}</Alert>}
          {!invoice.isLoading && !inv && (
            <Alert severity="info">
              No invoice has been raised for this order yet. Raising it numbers a document in your own invoice series, and it can be
              done once.
            </Alert>
          )}
          {inv && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h6">{inv.invoicenumber}</Typography>
              <Typography variant="body2" color="text.secondary">
                {inv.supplierlegalname}
                {inv.suppliergstin ? ` · ${inv.suppliergstin}` : ""}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                To {inv.buyername}
                {inv.buyergstin ? ` · ${inv.buyergstin}` : " · no GSTIN"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Place of supply {inv.placeofsupply}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Taxable {inr(Math.round(inv.taxablevalue * 100))}
              </Typography>
              <Typography variant="body2">
                GST {inr(Math.round(inv.totaltax * 100))}
                {inv.totaligst > 0 ? " (IGST)" : inv.totaltax > 0 ? " (CGST + SGST)" : ""}
              </Typography>
              <Typography variant="h6">Total {inr(Math.round(inv.totalamount * 100))}</Typography>
            </Paper>
          )}
          {raise.error && <Alert severity="error">{raiseRefusal(raise.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {!inv && !invoice.isLoading && (
          <Button variant="contained" disabled={raise.isPending} onClick={() => raise.mutate()}>
            Raise invoice
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

/**
 * An Internet Distributor's pin sale voucher (radbilling v0.1.112 via radaccess
 * v1.191). Like the invoice, reading never issues one. A voucher is not a tax
 * invoice; if the buyer later asks for one, the invoice replaces the voucher
 * and the voucher says so.
 */
function VoucherDialog({ ns, order, onClose }: { ns: string; order: PinOrder; onClose: () => void }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const key = ["pinorder-voucher", ns, order.id];
  const voucher = useQuery({
    queryKey: key,
    enabled: Boolean(identity.token && ns),
    queryFn: ({ signal }) => getPinOrderVoucher(identity, ns, order.id, signal),
    retry: false,
  });
  const issue = useMutation({
    mutationFn: () => issuePinOrderVoucher(identity, ns, order.id),
    onSuccess: (v: PinOrderVoucher) => qc.setQueryData(key, v),
  });

  const v = voucher.data;
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Voucher for {order.quantity} pins</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {shortName(order.buyer)} — {order.planname}, approved {when(order.decidedat)}
          </Typography>
          {voucher.isLoading && <Typography color="text.secondary">Loading…</Typography>}
          {voucher.error && <Alert severity="error">{errText(voucher.error)}</Alert>}
          {!voucher.isLoading && !v && (
            <Alert severity="info">
              No voucher has been issued for this order yet. Issuing it numbers a document in your own voucher series, and it can be
              done once. A voucher is not a tax invoice.
            </Alert>
          )}
          {v && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h6">{v.vouchernumber}</Typography>
              <Typography variant="body2" color="text.secondary">
                {v.sellerlegalname}
                {v.sellergstin ? ` · ${v.sellergstin}` : ""}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                To {v.buyername}
              </Typography>
              <Typography variant="body2">{v.description}</Typography>
              <Typography variant="h6" sx={{ mt: 1 }}>
                Total {inr(Math.round(v.amountgross * 100))}
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary">
                Issued {when(v.issuedat)}
              </Typography>
              {v.note && (
                <Typography variant="caption" display="block" color="text.secondary">
                  {v.note}
                </Typography>
              )}
              {v.replacedbynumber && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Replaced by tax invoice {v.replacedbynumber}
                  {v.replacedat ? ` on ${when(v.replacedat)}` : ""}.
                </Alert>
              )}
            </Paper>
          )}
          {issue.error && <Alert severity="error">{voucherRefusal(issue.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {!v && !voucher.isLoading && (
          <Button variant="contained" disabled={issue.isPending} onClick={() => issue.mutate()}>
            Issue voucher
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

/** Why a voucher could not be issued. radbilling's 409 reasons are kept. */
function voucherRefusal(e: unknown): string {
  const reason = errText(e);
  if (e instanceof ApiError && e.status === 409) {
    return `No voucher for this order: a licensed seller always invoices, the seller must be an Internet Distributor whose Owner accepted the declaration, and a sale already tax-invoiced takes no voucher. Server: ${reason}`;
  }
  if (e instanceof ApiError && e.status === 403) {
    return `Not allowed to issue a voucher for this order. Server: ${reason}`;
  }
  return `Could not issue the voucher. Server: ${reason}`;
}

/**
 * Why an invoice could not be raised, in words a parent can act on. The
 * server's own reason is kept, because it names the missing record.
 *
 * 403 is a RULE, not a fault: the invoicing company may not issue this
 * document -- most often because it holds no licence to invoice internet
 * access (SAC 998422). 500 here is almost always missing legal identity
 * (legal name, GSTIN, registered office) on one side or the other.
 */
function raiseRefusal(e: unknown): string {
  const reason = errText(e);
  if (e instanceof ApiError && e.status === 403) {
    return `Not allowed to issue this invoice. The invoicing company must hold a licence to invoice internet access (SAC 998422), or the plan must be sold under another SAC. Server: ${reason}`;
  }
  if (e instanceof ApiError && e.status === 409) {
    return `This order cannot be invoiced in its current state. Server: ${reason}`;
  }
  return `Could not raise the invoice. Check that both companies have a legal name and a registered office on record. Server: ${reason}`;
}

/* ------------------------------------------------------------------ parts */

function Section({ title, error, children }: { title: string; error: unknown; children: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        {title}
      </Typography>
      {error ? <Alert severity="error">{errText(error)}</Alert> : children}
    </Box>
  );
}

function OrderTable({
  orders,
  showParty,
  empty,
  onAct,
  onInvoice,
  onVoucher,
  documents = ["tax_invoice"],
}: {
  orders: PinOrder[];
  showParty: "buyer" | "seller";
  empty: string;
  onAct?: (o: PinOrder, action: "payment" | "approve" | "reject") => void;
  onInvoice?: (o: PinOrder) => void;
  onVoucher?: (o: PinOrder) => void;
  /** Documents this seller may issue, the default first (pinSaleDocuments). */
  documents?: PinSaleDocument[];
}) {
  if (orders.length === 0) return <Typography color="text.secondary">{empty}</Typography>;
  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Placed</TableCell>
            <TableCell>{showParty === "buyer" ? "Sub-operator" : "From"}</TableCell>
            <TableCell>Plan</TableCell>
            <TableCell align="right">Pins</TableCell>
            <TableCell align="right">Total</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Payment</TableCell>
            {onAct && <TableCell align="right">Action</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {orders.map((o) => {
            const can = pinOrderActions(o);
            return (
              <TableRow key={o.id}>
                <TableCell>{when(o.createdat)}</TableCell>
                <TableCell>{shortName(showParty === "buyer" ? o.buyer : o.seller)}</TableCell>
                <TableCell>
                  {o.planname}
                  <Typography variant="caption" display="block" color="text.secondary">
                    up to {o.speedmbps} Mbps, {o.termdays} days
                  </Typography>
                </TableCell>
                <TableCell align="right">{o.quantity}</TableCell>
                <TableCell align="right">
                  {isAccessOrder(o) ? (
                    "No charge"
                  ) : (
                    <>
                      {inr(o.grosspaise)}
                      <Typography variant="caption" display="block" color="text.secondary">
                        incl. GST {inr(o.gstpaise)}
                      </Typography>
                    </>
                  )}
                </TableCell>
                <TableCell>
                  <Chip size="small" color={pinOrderStatusColour(o.status)} label={PIN_ORDER_STATUS_LABEL[o.status]} />
                  {o.decisionreason && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      {o.decisionreason}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {o.paymentreference || "—"}
                  {o.paymentrecordedat && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      {when(o.paymentrecordedat)}
                    </Typography>
                  )}
                </TableCell>
                {onAct && (
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      {can.payment && (
                        <Button size="small" onClick={() => onAct(o, "payment")}>
                          Record payment
                        </Button>
                      )}
                      {can.approve && (
                        <Button size="small" variant="contained" onClick={() => onAct(o, "approve")}>
                          Approve
                        </Button>
                      )}
                      {can.reject && (
                        <Button size="small" color="error" onClick={() => onAct(o, "reject")}>
                          Reject
                        </Button>
                      )}
                      {canInvoicePinOrder(o) &&
                        documents.map((d, i) =>
                          d === "voucher"
                            ? onVoucher && (
                                <Button key={d} size="small" variant={i === 0 ? "outlined" : "text"} onClick={() => onVoucher(o)}>
                                  Voucher
                                </Button>
                              )
                            : onInvoice && (
                                <Button key={d} size="small" variant={i === 0 && documents.length > 1 ? "outlined" : "text"} onClick={() => onInvoice(o)}>
                                  Invoice
                                </Button>
                              ),
                        )}
                    </Stack>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function PinTable({ pins, showHolder, empty, onRevoke }: { pins: Pin[]; showHolder: boolean; empty: string; onRevoke?: (p: Pin) => void }) {
  if (pins.length === 0) return <Typography color="text.secondary">{empty}</Typography>;
  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Key</TableCell>
            {showHolder && <TableCell>Held by</TableCell>}
            <TableCell align="right">Up to</TableCell>
            <TableCell align="right">Term</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Used</TableCell>
            {onRevoke && <TableCell align="right">Action</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {pins.map((p) => (
            <TableRow key={p.id}>
              <TableCell sx={{ fontFamily: "monospace" }}>{p.key}</TableCell>
              {showHolder && <TableCell>{shortName(p.holder)}</TableCell>}
              <TableCell align="right">{p.speedmbps} Mbps</TableCell>
              <TableCell align="right">{p.termdays} days</TableCell>
              <TableCell>
                <Chip size="small" color={pinStatusColour(p.status)} label={PIN_STATUS_LABEL[p.status]} />
                {p.closedreason && (
                  <Typography variant="caption" display="block" color="text.secondary">
                    {p.closedreason}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                {p.usedat ? when(p.usedat) : "—"}
                {p.usedref && (
                  <Typography variant="caption" display="block" color="text.secondary">
                    {p.usedref}
                  </Typography>
                )}
              </TableCell>
              {onRevoke && (
                <TableCell align="right">
                  {canRevokePin(p) && (
                    <Button size="small" color="error" onClick={() => onRevoke(p)}>
                      Revoke
                    </Button>
                  )}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
